import React from 'react';

const SimpleChart = ({ data, type = 'line', height = 200 }) => {
  if (type === 'line') {
    return <LineChart data={data} height={height} />;
  } else if (type === 'doughnut') {
    return <DoughnutChart data={data} height={height} />;
  } else if (type === 'bar') {
    return <BarChart data={data} height={height} />;
  }
  
  return <LineChart data={data} height={height} />;
};

const LineChart = ({ data, height }) => {
  const maxValue = Math.max(...data.map(d => d.sales || d.value || 0));
  const minValue = Math.min(...data.map(d => d.sales || d.value || 0));
  const range = maxValue - minValue;
  
  const points = data.map((item, index) => {
    const x = (index / (data.length - 1)) * 300;
    const y = height - ((item.sales || item.value || 0) - minValue) / range * (height - 40);
    return { x, y, value: item.sales || item.value || 0, label: item.month || item.name };
  });

  const pathData = points.map((point, index) => 
    `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
  ).join(' ');

  return (
    <div className="w-full">
      <svg width="100%" height={height} viewBox={`0 0 300 ${height}`} className="overflow-visible">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => (
          <line
            key={index}
            x1="0"
            y1={height - ratio * (height - 40)}
            x2="300"
            y2={height - ratio * (height - 40)}
            stroke="#f3f4f6"
            strokeWidth="1"
          />
        ))}
        
        {/* Area under curve */}
        <path
          d={`${pathData} L 300 ${height} L 0 ${height} Z`}
          fill="url(#gradient)"
          opacity="0.3"
        />
        
        {/* Line */}
        <path
          d={pathData}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        
        {/* Points */}
        {points.map((point, index) => (
          <circle
            key={index}
            cx={point.x}
            cy={point.y}
            r="4"
            fill="#3b82f6"
            stroke="white"
            strokeWidth="2"
          />
        ))}
        
        {/* Gradient definition */}
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.8"/>
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.1"/>
          </linearGradient>
        </defs>
      </svg>
      
      {/* X-axis labels */}
      <div className="flex justify-between mt-2 text-xs text-gray-500">
        {data.map((item, index) => (
          <span key={index}>{item.month || item.name}</span>
        ))}
      </div>
    </div>
  );
};

const DoughnutChart = ({ data, height }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const centerX = height / 2;
  const centerY = height / 2;
  const radius = Math.min(centerX, centerY) - 20;
  const innerRadius = radius * 0.6;
  
  let currentAngle = 0;
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
  
  const segments = data.map((item, index) => {
    const percentage = item.value / total;
    const angle = percentage * 2 * Math.PI;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    
    const x1 = centerX + Math.cos(startAngle) * radius;
    const y1 = centerY + Math.sin(startAngle) * radius;
    const x2 = centerX + Math.cos(endAngle) * radius;
    const y2 = centerY + Math.sin(endAngle) * radius;
    
    const x3 = centerX + Math.cos(endAngle) * innerRadius;
    const y3 = centerY + Math.sin(endAngle) * innerRadius;
    const x4 = centerX + Math.cos(startAngle) * innerRadius;
    const y4 = centerY + Math.sin(startAngle) * innerRadius;
    
    const largeArcFlag = angle > Math.PI ? 1 : 0;
    
    const pathData = [
      `M ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      `L ${x3} ${y3}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
      'Z'
    ].join(' ');
    
    currentAngle += angle;
    
    return {
      path: pathData,
      color: colors[index % colors.length],
      percentage: Math.round(percentage * 100),
      label: item.name,
      value: item.value
    };
  });

  return (
    <div className="flex items-center">
      <div className="flex-1">
        <svg width={height} height={height} viewBox={`0 0 ${height} ${height}`}>
          {segments.map((segment, index) => (
            <path
              key={index}
              d={segment.path}
              fill={segment.color}
              stroke="white"
              strokeWidth="2"
            />
          ))}
        </svg>
      </div>
      
      {/* Legend */}
      <div className="ml-4 space-y-2">
        {segments.map((segment, index) => (
          <div key={index} className="flex items-center text-sm">
            <div
              className="w-3 h-3 rounded-full mr-2"
              style={{ backgroundColor: segment.color }}
            ></div>
            <span className="text-gray-700">{segment.label}</span>
            <span className="ml-auto text-gray-500">{segment.percentage}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const BarChart = ({ data, height }) => {
  const maxValue = Math.max(...data.map(d => d.value || d.sales || 0));
  const barWidth = 300 / data.length * 0.8;
  const barSpacing = 300 / data.length * 0.2;
  
  return (
    <div className="w-full">
      <svg width="100%" height={height} viewBox={`0 0 300 ${height}`}>
        {data.map((item, index) => {
          const barHeight = ((item.value || item.sales || 0) / maxValue) * (height - 40);
          const x = index * (barWidth + barSpacing) + barSpacing / 2;
          const y = height - barHeight;
          
          return (
            <rect
              key={index}
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              fill="#3b82f6"
              rx="4"
            />
          );
        })}
      </svg>
      
      <div className="flex justify-between mt-2 text-xs text-gray-500">
        {data.map((item, index) => (
          <span key={index}>{item.name || item.month}</span>
        ))}
      </div>
    </div>
  );
};

export default SimpleChart;
